import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import PageHead from '../components/PageHead';
import { APP_STORE_URL, APP_VERSION, PLAY_URL, SUPPORT_PHONE, SUPPORT_TEL } from '../config/site';
import { detectPlatform, Platform } from '../hooks/usePlatform';
import { usePageMeta } from '../hooks/usePageMeta';

type Mode = 'actualizar' | 'descargar';

const TEXT = {
  actualizar: {
    title: 'Actualiza la app · Violeta',
    description: 'Actualiza Violeta en tres pasos desde Google Play o el App Store. Tu cuenta no se borra al actualizar.',
    h1: 'Actualiza Violeta',
    crumb: 'Actualizar la app',
    lead: 'Tres pasos y listo. Tu cuenta no se borra.',
    qrNote: 'Este código abre esta página en tu teléfono.',
    qr: '/assets/qr-actualizar.svg',
    missing: '¿No te aparece «Actualizar»?',
  },
  descargar: {
    title: 'Descarga la app · Violeta',
    description: 'Descarga Violeta desde Google Play o el App Store en tres pasos.',
    h1: 'Descarga Violeta',
    crumb: 'Descargar la app',
    lead: 'Tres pasos y listo.',
    qrNote: 'Este código abre esta página en tu teléfono.',
    qr: '/assets/qr-descargar.svg',
    missing: '¿No te aparece «Instalar»?',
  },
};

// The App Store listing is older than the Android app, so no version number is promised on iPhone.
const Update: React.FC<{ mode: Mode }> = ({ mode }) => {
  const [platform] = useState<Platform>(detectPlatform);
  const text = TEXT[mode];
  usePageMeta(text.title, text.description);

  const storeName =
    platform === 'android' ? 'Google Play' : platform === 'ios' ? 'App Store' : 'la tienda de tu teléfono';

  return (
    <>
      <PageHead title={text.h1} crumb={text.crumb} lead={text.lead} />

      <div className="page-body">
        <div className="container">
          <div className="card-light dl-card">
            {platform === 'android' && (
              <div className="stack">
                <a className="btn btn--crimson btn--lg" href={PLAY_URL}>
                  <Icon name="google-play" />
                  Abrir Google Play
                </a>
                <p className="muted">Se abre la página de Violeta.</p>
              </div>
            )}

            {platform === 'ios' && (
              <div className="stack">
                <a className="btn btn--crimson btn--lg" href={APP_STORE_URL}>
                  <Icon name="apple" />
                  Abrir el App Store
                </a>
                <p className="muted">Se abre la página de Violeta.</p>
              </div>
            )}

            {platform === 'other' && (
              <>
                <p className="lead">Abre esta página en tu teléfono, o escanea este código con la cámara:</p>
                <div className="qr-img">
                  <img
                    src={text.qr}
                    alt="Código QR que abre esta página en tu teléfono"
                    width={300}
                    height={300}
                  />
                </div>
                <p className="muted">{text.qrNote}</p>
                <div className="row">
                  <a className="btn btn--outline" href={PLAY_URL}>Google Play (Android)</a>
                  <a className="btn btn--outline" href={APP_STORE_URL}>App Store (iPhone)</a>
                </div>
              </>
            )}

            <ol className="stepper">
              <li>
                <span>
                  {platform === 'other' ? (
                    <>
                      Escanea el código con tu teléfono, o abre {storeName} y busca <b>Violeta App</b>.
                    </>
                  ) : (
                    <>
                      Toca el botón de arriba. Se abre <b>{storeName}</b>.
                    </>
                  )}
                </span>
              </li>
              <li>
                <span>
                  {mode === 'actualizar' ? (
                    <>
                      Si ves el botón <b>Actualizar</b>, tócalo y espera a que termine. Si dice <b>Abrir</b>, ya tienes
                      la versión más reciente.
                    </>
                  ) : (
                    <>
                      Toca <b>Instalar</b> y espera a que termine. Si dice <b>Abrir</b>, ya la tienes.
                    </>
                  )}
                </span>
              </li>
              <li>
                <span>
                  {mode === 'actualizar' ? (
                    <>Abre Violeta. Tu cuenta no se borra al actualizar.</>
                  ) : (
                    <>Abre Violeta y sigue las instrucciones de la pantalla.</>
                  )}
                </span>
              </li>
            </ol>

            <p className="muted">Si puedes, usa wifi: así no gastas tus datos.</p>

            {mode === 'actualizar' && platform === 'android' && (
              <div className="callout">
                <Icon name="info" />
                <p>
                  <strong>¿Cómo sé si ya está actualizada?</strong> Inicia sesión, toca el círculo con tus iniciales,
                  luego <b>Ajustes</b>. Abajo dice <b>Versión</b>: debe ser <b>{APP_VERSION}</b> o más nueva.
                </p>
              </div>
            )}

            {mode === 'actualizar' && (
              <div className="callout">
                <Icon name="lock" />
                <p>
                  <strong>¿No puedes entrar?</strong> En la pantalla de inicio de sesión toca{' '}
                  <b>¿Olvidaste tu contraseña?</b> y te enviamos un correo para crear una nueva. Nunca compartas tu
                  contraseña, tampoco en capturas de pantalla.
                </p>
              </div>
            )}

            <details className="more">
              <summary>{text.missing}</summary>
              <div>
                <ol>
                  <li>Revisa que tengas internet (wifi o datos).</li>
                  <li>Cierra {platform === 'other' ? 'la tienda' : storeName} por completo y ábrela otra vez.</li>
                  <li>Espera unas horas: las actualizaciones llegan a los teléfonos poco a poco.</li>
                  <li>
                    Si sigue igual, llámanos al <a href={SUPPORT_TEL}>{SUPPORT_PHONE}</a> y te ayudamos. Este número no
                    es una línea de emergencias.
                  </li>
                </ol>
              </div>
            </details>

            <p className="muted">
              <Link to="/">Volver al inicio</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Update;
